import type React from "react";
import { cloneElement } from "react";
import { twJoin } from "tailwind-merge";

import { cardSurfaceVariants } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import type { RadioProps } from "@/design-system/Radio";
import { Radio } from "@/design-system/Radio";
import { Stack } from "@/design-system/Stack";
import { twResolve } from "@/helpers/tw-resolve";

import type { IconComponent } from "../Icon/Icon";

export type RadioCardProps = Omit<RadioProps, "className"> & {
  label: string;
  description?: string;
  icon?: IconComponent;
  children?: React.ReactNode;
};

export function RadioCard({
  label,
  description,
  icon,
  children,
  disabled,
  ...otherProps
}: RadioCardProps): React.ReactNode {
  return (
    <label
      className={twJoin(
        cardSurfaceVariants({ borderTone: "secondary", interactive: !disabled }),
        twResolve(
          "flex max-w-full flex-1 flex-col gap-space-sm p-space-md transition-colors duration-200 hover:border-selection-border-hover has-focus-visible:ring-2 has-focus-visible:ring-border-focus has-focus-visible:ring-offset-2",
          (otherProps.defaultChecked || otherProps.checked) &&
            "border-selection-border-hover bg-selection-selected-surface",
          disabled && "cursor-not-allowed border-transparent bg-background-secondary **:text-text-on-disabled",
        ),
      )}
      aria-disabled={disabled}
    >
      <Inline gap="sm" align="start" wrap={false}>
        <span className="flex h-icon-prominent items-center">
          <Radio {...otherProps} disabled={disabled} />
        </span>
        <Stack gap="xs">
          <span className="flex min-h-icon-prominent items-center gap-space-xs">
            {icon && cloneElement(icon, { size: 20 })}
            <span className="text-body-emphasis">{label}</span>
          </span>
          {description && <p className="text-caption text-text-primary">{description}</p>}
        </Stack>
      </Inline>
      {children}
    </label>
  );
}
